import React, { useEffect, useRef } from 'react';
import { formStaticCssVars } from '@eaco/forms-core';
import { useElementWidth } from '../lib/useElementWidth';
import { usePreviewFormContext } from './PreviewFormContext';

// Static forms design tokens as CSS vars — mirrors DocumentView so the frame
// *chrome* (rail, header, footer) resolves `var(--forms-accent,…)` too. In the
// pageless layout the frame wraps DocumentView (which injects its own copy for
// the body), so without this the footer's accent Submit button would render on
// a dead `var()` (transparent). Only sets the `-default` fallback, so a
// per-form FormThemeProvider accent still wins where present.
const STATIC_TOKEN_VARS = formStaticCssVars() as React.CSSProperties;

/**
 * Shared desktop fill shell: a persistent left **rail** + a centered
 * **answer column**, or a single-column fallback below the breakpoint.
 *
 * It is purely presentational — the caller supplies every region as a slot,
 * so both the paged (`Pages`) and pageless (`PagelessFillFrame`) surfaces get
 * the identical two-column chrome:
 *
 *   rail:   brand (top) · rail (stepper / empty) · progress (bottom)
 *   column: header (+ status) · body (scrolls) · footer (+ pre-submit)
 *
 * The wide/narrow switch is driven by the frame's own measured width (see
 * useElementWidth) — a wide full-page card gets the rail, a narrow modal or
 * phone gets the caller's `narrow` fallback — with no viewport media query
 * and no host branching. The host-owned autosave indicator arrives via
 * PreviewFormContext (`statusSlot`) and is placed in the header here; the
 * pre-submit widget (Turnstile) is composed into the caller's `footer` so it
 * only shows on the step that actually submits.
 */

const RAIL_WIDTH = 262;
// Card interior width at/above which the rail shows. Chosen to sit between the
// two surfaces that must stay single-column — the 768px in-app dialog and the
// tablet bezel — and the ones that should get the rail: the full-page filler
// (~950px) and the designer's desktop preview (~870px, narrowed by the editor
// chrome). At this width the rail (262px) still leaves a ~560px answer column.
const DEFAULT_MIN_WIDTH = 820;

interface Props {
  /** Rail top — the brand tile (logo + form/company name). */
  brand: React.ReactNode;
  /** Rail middle — the vertical stepper, or nothing (pageless = empty). */
  rail?: React.ReactNode;
  /** Rail bottom — a progress block (already padded/bordered), or nothing. */
  progress?: React.ReactNode;
  /** Answer-column header (e.g. "STEP n OF N" + page name), or nothing. */
  header?: React.ReactNode;
  /** Scrolling answer body — the fields. Held to a ~560px measure. */
  body: React.ReactNode;
  /** Answer-column footer (Prev/Next, or a single Submit), or nothing. */
  footer?: React.ReactNode;
  /** The single-column layout rendered below the breakpoint. */
  narrow: React.ReactNode;
  /** Card width (px) at/above which the rail shows. Default 960. */
  minWidth?: number;
  /**
   * When this changes, the scroll region resets to the top — e.g. the paged
   * caller passes the current page index so a Next/Back jump starts each step
   * from the top instead of mid-scroll. Omit for surfaces without paging.
   */
  bodyScrollKey?: string | number;
}

export const FillDesktopFrame: React.FC<Props> = ({
  brand,
  rail,
  progress,
  header,
  body,
  footer,
  narrow,
  minWidth = DEFAULT_MIN_WIDTH,
  bodyScrollKey,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const bodyScrollRef = useRef<HTMLDivElement>(null);
  const narrowScrollRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(rootRef);
  const wide = width >= minWidth;

  // Reset whichever scroll region is mounted to the top when the page changes,
  // so a step jump doesn't land mid-scroll. Both refs are read null-safely
  // since only one layout is mounted at a time.
  useEffect(() => {
    bodyScrollRef.current?.scrollTo({ top: 0 });
    narrowScrollRef.current?.scrollTo({ top: 0 });
  }, [bodyScrollKey]);

  const ctx = usePreviewFormContext();
  const statusSlot = ctx?.statusSlot;

  return (
    <div
      ref={rootRef}
      className="flex min-h-0 flex-1 flex-col"
      style={STATIC_TOKEN_VARS}
    >
      {wide ? (
        <div className="flex min-h-0 flex-1">
          {/* LEFT RAIL */}
          <div
            className="flex flex-none flex-col border-r border-[#edf0f4] bg-[#f7f9fc]"
            style={{ width: RAIL_WIDTH }}
          >
            {brand}
            <div className="min-h-0 flex-1 overflow-y-auto px-[14px] py-[16px]">
              {rail}
            </div>
            {progress}
          </div>

          {/* RIGHT ANSWER COLUMN */}
          <div className="flex min-w-0 flex-1 flex-col">
            {(header || statusSlot) && (
              // Same fixed height as the rail brand tile so both bottom
              // dividers line up into one line across the card.
              <div className="flex h-[74px] flex-none items-center gap-[12px] border-b border-[#edf0f4] px-[26px]">
                <div className="min-w-0 flex-1">{header}</div>
                {statusSlot && <div className="flex-none">{statusSlot}</div>}
              </div>
            )}
            <div
              ref={bodyScrollRef}
              className="min-h-0 flex-1 overflow-y-auto px-[26px] py-[22px]"
            >
              <div className="flex max-w-[560px] flex-col gap-[18px]">{body}</div>
            </div>
            {/* The caller owns the footer's internal layout (button row +
                any pre-submit widget on the last step). */}
            {footer && (
              <div className="flex-none border-t border-[#edf0f4]">{footer}</div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* The status (autosave, live score) sits in the brand band on
              narrow screens; see FormBrandHeader. */}
          {/* Own the scroll here (the host no longer wraps us in one) so the
              narrow layout's sticky top stepper + bottom footer still pin. */}
          <div
            ref={narrowScrollRef}
            className="flex min-h-0 flex-1 flex-col overflow-y-auto"
          >
            {narrow}
          </div>
        </div>
      )}
    </div>
  );
};

export default FillDesktopFrame;
